import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { getUserDetails } from "../platform-components/utils/shared-data.service";
import { getData, apiPaths, getInstutueApp } from "../platform-components/api-integration-util";
import { PreLoader } from "../platform-components/PreLoader";
import "./team-performance.css";

const ACTION_TYPE_CONFIG = {
  QUESTION_CREATED: { label: "Question Created", badge: "created", icon: "+" },
  "SENT FOR APPROVAL": { label: "Sent for Review", badge: "review", icon: "S" },
  APPROVED: { label: "Approved", badge: "approved", icon: "\u2713" },
  REJECTED: { label: "Rejected", badge: "rejected", icon: "\u2717" },
  "NEED CORRECTION": { label: "Correction Requested", badge: "review", icon: "!" },
  ANSWER_CONFLICT_RESOLVED: { label: "Answer Conflict Resolved", badge: "conflict", icon: "A" },
  CLASSIFICATION_RESOLVED: { label: "Classification Reviewed", badge: "conflict", icon: "C" },
  VARIATION_CREATED: { label: "Variation Created", badge: "variation", icon: "V" },
  VARIATION_REVIEWED: { label: "Variation Reviewed", badge: "approved", icon: "R" },
  RESTYLE_RESTYLE_APPLIED: { label: "Restyle Applied", badge: "restyle", icon: "R" },
  RESTYLE_NEW_QUESTION_GENERATED: { label: "Question Generated", badge: "restyle", icon: "G" },
};

function getActionConfig(actionType) {
  return (
    ACTION_TYPE_CONFIG[actionType] || {
      label: actionType,
      badge: "review",
      icon: "?",
    }
  );
}

function formatDateTime(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString();
}

// Ensures end date covers the full day (23:59:59) for DATETIME column comparisons
function toApiDateRange(start, end) {
  return {
    startDate: start.length === 10 ? start + " 00:00:00" : start,
    endDate: end.length === 10 ? end + " 23:59:59" : end,
  };
}

// Tab definitions: key, label, source (qb or inst), category param, stat keys to show
const TABS = [
  {
    key: "questions",
    label: "Questions",
    source: "qb",
    category: "QUESTIONS_CREATED",
    statKeys: [{ key: "questions_created", label: "Created" }],
  },
  {
    key: "reviews",
    label: "Reviews",
    source: "qb",
    category: "STATUS_AUDIT",
    statKeys: [
      { key: "sent_for_review", label: "Sent for Review" },
      { key: "approved", label: "Approved" },
      { key: "rejected", label: "Rejected" },
      { key: "correction_requested", label: "Corrections" },
    ],
  },
  {
    key: "answer_conflicts",
    label: "Answer Conflicts",
    source: "inst",
    category: "ANSWER_CONFLICTS",
    statKeys: [
      { key: "answer_conflicts_resolved", label: "Resolved" },
      { key: "answer_conflicts_dismissed", label: "Dismissed" },
    ],
  },
  {
    key: "classification",
    label: "Classification",
    source: "inst",
    category: "CLASSIFICATION",
    statKeys: [
      { key: "classification_resolved", label: "Resolved" },
      { key: "classification_dismissed", label: "Dismissed" },
    ],
  },
  {
    key: "variations",
    label: "Variations",
    source: "inst",
    category: "VARIATIONS",
    statKeys: [
      { key: "variations_created", label: "Created" },
      { key: "variations_approved", label: "Approved" },
      { key: "variations_rejected", label: "Rejected" },
    ],
  },
  {
    key: "restyles",
    label: "Restyles",
    source: "inst",
    category: "RESTYLES",
    statKeys: [
      { key: "restyles_applied", label: "Applied" },
      { key: "questions_generated", label: "Generated" },
    ],
  },
];

const PAGE_SIZE = 20;

export default function TeamMemberDetail({
  memberEmail,
  startDate,
  endDate,
  onBack,
}) {
  const [statsLoading, setStatsLoading] = useState(true);
  const [qbStats, setQbStats] = useState({});
  const [instStats, setInstStats] = useState({});
  const [activeTab, setActiveTab] = useState("questions");

  // Per-tab activity state: { [tabKey]: { activities, totalCount, page, totalPages, loading, loaded } }
  const [tabData, setTabData] = useState({});

  // Fetch stats on mount (lightweight — no timeline)
  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberEmail, startDate, endDate]);

  // Load first tab's activity after stats load
  useEffect(() => {
    if (!statsLoading) {
      loadTabActivity("questions", 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsLoading, loadTabActivity]);

  async function fetchStats() {
    setStatsLoading(true);
    setTabData({});
    try {
      const headers = {
        tenantId: getUserDetails().tenantId,
        userEmail: getUserDetails().email,
      };
      const dateRange = toApiDateRange(startDate, endDate);
      const params = { memberEmail, ...dateRange };

      const [qbRes, instRes] = await Promise.all([
        getData(apiPaths.teamPerformanceMemberDetail, { headers, params }),
        getInstutueApp(apiPaths.teamPerformanceInstMemberDetail, params, headers),
      ]);

      setQbStats(qbRes?.result?.stats || {});
      setInstStats(instRes?.result?.stats || {});
    } catch (e) {
      console.error("Error fetching member stats", e);
      toast.error("Failed to load member stats");
    }
    setStatsLoading(false);
  }

  const loadTabActivity = useCallback(async (tabKey, page) => {
    const tab = TABS.find((t) => t.key === tabKey);
    if (!tab) return;

    // Mark loading
    setTabData((prev) => ({
      ...prev,
      [tabKey]: {
        ...(prev[tabKey] || {}),
        loading: true,
      },
    }));

    try {
      const headers = {
        tenantId: getUserDetails().tenantId,
        userEmail: getUserDetails().email,
      };
      const dateRange = toApiDateRange(startDate, endDate);
      const params = {
        memberEmail,
        ...dateRange,
        category: tab.category,
        page,
        pageSize: PAGE_SIZE,
      };

      let res;
      if (tab.source === "qb") {
        res = await getData(apiPaths.teamPerformanceMemberActivity, { headers, params });
      } else {
        res = await getInstutueApp(apiPaths.teamPerformanceInstMemberActivity, params, headers);
      }

      const result = res?.result || {};
      setTabData((prev) => ({
        ...prev,
        [tabKey]: {
          activities: result.activities || [],
          totalCount: result.totalCount || 0,
          page: result.page || 0,
          totalPages: result.totalPages || 0,
          loading: false,
          loaded: true,
        },
      }));
    } catch (e) {
      console.error("Error fetching activity for tab " + tabKey, e);
      toast.error("Failed to load " + tab.label + " activity");
      setTabData((prev) => ({
        ...prev,
        [tabKey]: {
          ...(prev[tabKey] || {}),
          loading: false,
          loaded: true,
          activities: [],
          totalCount: 0,
          page: 0,
          totalPages: 0,
        },
      }));
    }
  }, [memberEmail, startDate, endDate]);

  function handleTabClick(tabKey) {
    setActiveTab(tabKey);
    // Lazy load: only fetch if not already loaded
    if (!tabData[tabKey] || !tabData[tabKey].loaded) {
      loadTabActivity(tabKey, 0);
    }
  }

  function handlePageChange(tabKey, newPage) {
    loadTabActivity(tabKey, newPage);
  }

  const allStats = { ...qbStats, ...instStats };
  const currentTab = TABS.find((t) => t.key === activeTab);
  const currentData = tabData[activeTab] || {};

  if (statsLoading) {
    return (
      <div className="team-perf-loading">
        <PreLoader />
      </div>
    );
  }

  return (
    <div className="member-detail-container">
      <button className="member-detail-back" onClick={onBack}>
        &larr; Back to Team Performance
      </button>

      <div className="member-detail-header">
        <h2>{memberEmail}</h2>
        <span style={{ fontSize: 13, color: "#777" }}>
          {startDate} to {endDate}
        </span>
      </div>

      {/* Stats Summary Cards */}
      <div className="member-detail-stats">
        {TABS.map((tab) =>
          tab.statKeys.map((sk) => (
            <div className="member-detail-stat" key={sk.key}>
              <div className="stat-value">{Number(allStats[sk.key] || 0)}</div>
              <div className="stat-label">{sk.label}</div>
            </div>
          ))
        )}
      </div>

      {/* Tab Navigation */}
      <div className="member-tabs">
        {TABS.map((tab) => {
          const tabCount = tab.statKeys.reduce(
            (sum, sk) => sum + Number(allStats[sk.key] || 0),
            0
          );
          return (
            <button
              key={tab.key}
              className={`member-tab ${activeTab === tab.key ? "active" : ""}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
              {tabCount > 0 && (
                <span className="tab-count">{tabCount}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab Content — Activity Timeline */}
      <div className="member-tab-content">
        {currentTab && (
          <>
            {/* Tab-specific stat badges */}
            <div className="tab-stat-badges">
              {currentTab.statKeys.map((sk) => (
                <span className="tab-stat-badge" key={sk.key}>
                  {sk.label}: <strong>{Number(allStats[sk.key] || 0)}</strong>
                </span>
              ))}
            </div>

            {currentData.loading ? (
              <div className="team-perf-loading" style={{ minHeight: 120 }}>
                <PreLoader />
              </div>
            ) : !currentData.loaded ? (
              <div className="team-perf-no-data">Click to load activity.</div>
            ) : currentData.activities && currentData.activities.length > 0 ? (
              <>
                <div className="timeline-info">
                  Showing {currentData.activities.length} of{" "}
                  {currentData.totalCount} actions (Page{" "}
                  {currentData.page + 1} of {currentData.totalPages})
                </div>
                <ul className="timeline-list">
                  {currentData.activities.map((item, idx) => {
                    const config = getActionConfig(item.action_type);
                    return (
                      <li key={idx} className="timeline-item">
                        <div className={`timeline-badge ${config.badge}`}>
                          {config.icon}
                        </div>
                        <div className="timeline-content">
                          <div className="timeline-action">{config.label}</div>
                          <div className="timeline-detail">
                            {item.question_id && (
                              <span>Question #{item.question_id}</span>
                            )}
                            {item.question_preview && (
                              <span> - {item.question_preview}</span>
                            )}
                            {item.detail && !item.question_preview && (
                              <span> - {item.detail}</span>
                            )}
                            {item.comment && (
                              <span> | Comment: {item.comment}</span>
                            )}
                            {item.status && (
                              <span
                                style={{
                                  marginLeft: 8,
                                  fontSize: 11,
                                  padding: "1px 6px",
                                  borderRadius: 3,
                                  background: "#f0f0f0",
                                  color: "#555",
                                }}
                              >
                                {item.status}
                              </span>
                            )}
                          </div>
                          <div className="timeline-date">
                            {formatDateTime(item.action_date)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Pagination Controls */}
                {currentData.totalPages > 1 && (
                  <div className="timeline-pagination">
                    <button
                      disabled={currentData.page === 0}
                      onClick={() =>
                        handlePageChange(activeTab, currentData.page - 1)
                      }
                    >
                      &laquo; Previous
                    </button>
                    <span className="page-info">
                      Page {currentData.page + 1} of {currentData.totalPages}
                    </span>
                    <button
                      disabled={
                        currentData.page >= currentData.totalPages - 1
                      }
                      onClick={() =>
                        handlePageChange(activeTab, currentData.page + 1)
                      }
                    >
                      Next &raquo;
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="team-perf-no-data">
                No {currentTab.label.toLowerCase()} activity found for this
                member in the selected period.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
